// @ts-strict-ignore
import {
	computed,
	Directive,
	DoCheck,
	ElementRef,
	HostListener,
	Injector,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	Signal,
	SimpleChanges,
} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {
	SimConfigurationService,
	SimEnumExtendedService,
	SimExportService,
	SimMaterialDialogService,
	NotificationService,
	SimSharedAudioDataService,
	SimUtilityService,
	SimUtilityExtendedService,
} from "@services";
import {AudioChannelState, CameraLayoutType, CameraViewMode, IVideoScreenPlayResult, RecordingScene, ScenarioRecording} from "@models";
import {TranslateService} from "@ngx-translate/core";
import Enumerable from "linq";
import dayjs from "dayjs";
import {PlayResult} from "@models";
import {IVideoScreen} from "@models";
import {Unsubscribable} from "rxjs";
import {Translations} from "@translations";
import {TimeProperties} from "../../../modules/web-manager/subModules/scenario-archive/components/scenario-archive-base";
import {environment} from "../../../../environments/environment";

@Directive()
export abstract class BaseRecordingPlayer implements OnInit, OnChanges, OnDestroy, DoCheck {
	T = Translations;
	CameraViewMode = CameraViewMode;

	@Input() public scenarioRecording: ScenarioRecording;

	@Input() public cameraViewMode = CameraViewMode.AllScreens;
	@Input() public scenarioRecordingExportId: string = null;
	public screens: IVideoScreen[] = [];
	public scenarioRecordingScene: RecordingScene = null;

	@Input() public isPlaying: boolean = false;
	@Input() public lastJumpToPosition: number = null;
	@Input() public playbackRate: number = 1;

	@Input() public audioChannelStates: {[key: string]: AudioChannelState} = {};

	protected isJumping = false;
	// AUDIO DELAY CONFIG
	protected audioDelayTimeout: ReturnType<typeof setTimeout> = null;
	protected waitingForAudioTimeout = false;
	protected startPlayingTask: Promise<any> = null;
	private sceneIndex = 0;
	private enableDebug = true;
	private audio: HTMLAudioElement = null;
	// SYNCING CONFIG
	private syncing = false;
	private syncThread: ReturnType<typeof setInterval> = null;
	private timerThread: ReturnType<typeof setInterval> = null;

	private synchronizationMasterScreen: IVideoScreen = null;
	private audioOffset = 0; // ms
	private handleAudioErrorEventsFunction: EventListenerOrEventListenerObject = null;
	private unsubscribePlayerCommand: Unsubscribable = null;

	// This member is used to transport the time information to the parent component
	public readonly timeProperties = new TimeProperties();
	private startPlaying = false;
	private waitPausing = false;
	private _simNotificationService: NotificationService;
	private _simUtilityService: SimUtilityExtendedService;
	protected _emitterService: EventEmitterService;
	private translate: TranslateService;
	private _simEnumService: SimEnumExtendedService;
	private _simExportService: SimExportService;
	private _simMaterialDialogService: SimMaterialDialogService;
	private _simSharedAudioDataService: SimSharedAudioDataService;
	protected audioContext: AudioContext = null;
	protected audioSource: MediaElementAudioSourceNode = null;
	protected audioSplitter: ChannelSplitterNode = null;
	protected gainNodes: GainNode[] = null;
	protected analyserNodes: AnalyserNode[] = null;
	protected audioMerger: ChannelMergerNode = null;
	protected audioWorkletNode: AudioWorkletNode = null;
	protected audioMasterGainNode: GainNode = null;
	protected audioDynamicCompressorNode: DynamicsCompressorNode = null;
	protected calculateVolumeInterval: ReturnType<typeof setInterval> | number = null;

	protected constructor(protected _injector: Injector) {
		this._simNotificationService = this._injector.get(NotificationService);
		this._simUtilityService = this._injector.get(SimUtilityExtendedService);
		this._emitterService = this._injector.get(EventEmitterService);
		this.translate = this._injector.get(TranslateService);
		this._simEnumService = this._injector.get(SimEnumExtendedService);
		this._simExportService = this._injector.get(SimExportService);
		this._simMaterialDialogService = this._injector.get(SimMaterialDialogService);
		this._simSharedAudioDataService = this._injector.get(SimSharedAudioDataService);
	}

	ngDoCheck(): void {
		this.updateAudioChannelStates();
	}

	public async ngOnChanges(changes: SimpleChanges) {
		if (changes) {
			if (
				changes["scenarioRecording"] &&
				(changes["scenarioRecording"]?.previousValue as ScenarioRecording)?.id !== this.scenarioRecording?.id
			) {
				const scenarioRecordingIsNotNull = !!this.scenarioRecording && !this._simUtilityService.isEmpty(this.scenarioRecording);
				const scenarioRecordingExportIdIsNotNull = !!this.scenarioRecordingExportId;

				if (scenarioRecordingIsNotNull && scenarioRecordingExportIdIsNotNull) {
					if (this.scenarioRecordingExportId && !this.scenarioRecording["isTransformedForExport"]) {
						// ------------------------------------------------------- //
						//  /\_/\  (                                               //
						// ( ^.^ ) _)                                              //
						//   \"/  (                                                //
						//  ( | | )                                                //
						// (__d b__)                                               //
						// ------------------------------------------------------- //
						// We want to view an export so we have to modify the      //
						// scenario recording accordingly. This will trigger       //
						// another $onChanges event. Which then will initialize    //
						// the video.                                              //
						// ------------------------------------------------------- //

						this._simExportService.getExport(this.scenarioRecordingExportId).then(async (getExportResult) => {
							if (getExportResult) {
								const scenarioRecordingExport = getExportResult;

								const recordingScenes = this.scenarioRecording.recordingScenes;
								const firstRecordingScene = recordingScenes[0];
								const lastRecordingScene = recordingScenes[recordingScenes.length - 1];

								const totalRecordingSceneTimeMs = dayjs(lastRecordingScene.end).diff(
									dayjs(firstRecordingScene.start),
									"milliseconds",
								);

								this.scenarioRecording.recordingScenes = [
									{
										recordingSceneId: this.scenarioRecordingExportId,
										start: firstRecordingScene.start,
										end: lastRecordingScene.end,
										totalRecordingSceneTimeMs: totalRecordingSceneTimeMs,
										index: 0,
										cameraLayout: CameraLayoutType.Single,
										cameraLayoutPresetIndex: 0,
										cameraLayoutOption: null,
										description: "Export: " + scenarioRecordingExport.config.resolution,
										audioFiles: null,
										videoFiles: [
											{
												fileName: scenarioRecordingExport.id + ".mp4",
												abortedAt: null,
												cameraId: null,
												duration: null,
												erroredAt: null,
												recordingServerFilePath: null,
												recordingDataFilePath: null,
												targetFileName: null,
												recordingServerFileName: null,
												videoIndex: 0,
												fileSize: null,
												firstResponseAt: null,
												isValid: true,
												lastResponseAt: null,
												processCounter: 0,
												processId: 0,
												startedAt: null,
												stoppedAt: null,
												videoRecordingId: null,
												timedOutAt: null,
												validatedAt: null,
												downloaded: true,
												posterCreated: false,
												lengthAdapted: false,
												posterFilePath: null,
												videoFileId: null,
											},
										],
										vitalMonitorIndex: null,
										trainingRoomId: null,
										recordingServerRecordingId: null,
										audioServerRecordingId: null,
									},
								];

								// This scenario recording is now transformed
								this.scenarioRecording["isTransformedForExport"] = true;

								// Reset scene index
								this.sceneIndex = 0;
								this.scenarioRecordingScene = this.getRecordingSceneByIndex(this.sceneIndex);

								await this.initializeScenarioRecording();
							}
						});
					} else {
						// Reset scene index
						this.sceneIndex = 0;
						this.scenarioRecordingScene = this.getRecordingSceneByIndex(this.sceneIndex);

						await this.initializeScenarioRecording();
					}
				} else if (scenarioRecordingIsNotNull) {
					// Reset scene index
					this.sceneIndex = 0;
					this.scenarioRecordingScene = this.getRecordingSceneByIndex(this.sceneIndex);

					await this.initializeScenarioRecording();
				} else {
					this.resetScenarioRecordingPlayerState();
				}
				this.updateAudioChannelStates();
			}

			if (changes["audioChannelStates"]) {
				this.updateAudioChannelStates();
			}

			if (changes["playbackRate"]) {
				this.updatePlaybackRate();
			}

			if (changes["isPlaying"] && changes["isPlaying"].previousValue !== changes["isPlaying"].currentValue) {
				if (this.isPlaying) {
					await this.play();
				} else {
					this.pause();
				}
			}

			if (
				changes["lastJumpToPosition"] &&
				changes["lastJumpToPosition"].previousValue !== changes["lastJumpToPosition"].currentValue
			) {
				await this.jumpTo(this.lastJumpToPosition, this.isPlaying);
			}
		}
	}

	private updateAudioChannelStates() {
		if (this.scenarioRecording) {
			if (this.audioChannelStates) {
				if (this.scenarioRecording.isMultiChannelAudio) {
					let gainNodesDict = Enumerable.from(this.gainNodes).toDictionary(
						(x) => x["audioInputChannelId"],
						(x) => x,
					);
					for (const audioInputChannelId of Object.keys(this.audioChannelStates)) {
						let foundGainNode = gainNodesDict.get(audioInputChannelId);
						if (foundGainNode) {
							if (!this.audioChannelStates[audioInputChannelId].muted) {
								SimUtilityService.setGainNodeValue(
									foundGainNode,
									this.audioChannelStates[audioInputChannelId].actualVolume,
								);
							} else {
								SimUtilityService.setGainNodeValue(foundGainNode, 0);
							}
						}
					}
				} else {
					let state = this.audioChannelStates?.[SimConfigurationService.settings.singleChannelId];
					if (state) {
						if (!state.muted) {
							this.setMasterVolume(state?.actualVolume);
						} else {
							this.setMasterVolume(0);
						}
					}
				}
			}
		}
	}

	private async initializeScenarioRecording() {
		// This method can be called multiple times in one life-cycle.
		// So to prevent attaching multiple event handlers, we can
		// dispose them here.
		this.disposeEventListeners();

		this.initializeTimes();

		await this.initializeMedias();
	}

	protected updatePlaybackRate() {
		if (this.screens && this.screens.length > 0) {
			for (const screen of this.screens) {
				screen.setPlaybackRate(this.playbackRate ?? 1);
			}
		}
		if (this.audio) {
			this.audio.playbackRate = this.playbackRate ?? 1;
		}
	}

	public ngOnInit() {
		this.calculateVolumes();
	}

	public ngOnDestroy(): void {
		this.disposeMediaStreams();

		// TODO
		// this.$window.document.removeEventListener("visibilitychange", this.$onVisibilityChangeHandler);

		this.disposeAudioGraph(true);
		this.disposeMediaStreams();
		this.disposeEventListeners();
		this.disposeThreads();
	}

	// in case of successfully jumping to the position.
	public jumpTo(millisecond, playAfterJumpComplete = true, userInitiated = false) {
		return new Promise((resolve) => {
			this.isJumping = true;

			this.pause(userInitiated);

			let maxTries = 50;

			const syncChecker = setInterval(() => {
				if (!this.syncing && maxTries > 0) {
					clearInterval(syncChecker);

					const lastSceneIndex = this.sceneIndex;

					this.sceneIndex = this.getSceneIndex(millisecond);
					this.scenarioRecordingScene = this.getRecordingSceneByIndex(this.sceneIndex);

					let initializeMediaTask: Promise<any> = null;

					if (lastSceneIndex !== this.sceneIndex) {
						// load videos for other scene
						initializeMediaTask = this.initializeMedias();
					} else {
						initializeMediaTask = Promise.resolve();
					}

					initializeMediaTask.then(() => {
						this.seek(millisecond, playAfterJumpComplete);

						this.isJumping = false;

						resolve(true);
					});
				} else if (maxTries > 0) {
					maxTries--;
				} else {
					clearInterval(syncChecker);

					resolve(false);
				}
			}, 100);
		}).then((success: boolean) => {
			this._emitterService.onScenarioRecordingPlayerJumped.next();

			return success;
		});
	}

	@HostListener("window:unload")
	private unload() {
		this.disposeAudioGraph();
		this.disposeMediaStreams();
	}

	protected handleAudioErrorEvents(event: UIEvent) {
		if (event.type !== "suspend") {
			console.debug("Audio event happened");
			console.debug(event);
		}
	}

	private disposeMediaStreams() {
		if (this.audio) {
			// Deregister eventhandler
			if (this.audio.removeEventListener) {
				this.audio.removeEventListener("abort", this.handleAudioErrorEventsFunction);
				this.audio.removeEventListener("cancel", this.handleAudioErrorEventsFunction);
				this.audio.removeEventListener("error", this.handleAudioErrorEventsFunction);
				this.audio.removeEventListener("stalled", this.handleAudioErrorEventsFunction);
				this.audio.removeEventListener("suspend", this.handleAudioErrorEventsFunction);
			}

			// Set source to null, this should cancel the audio playback.
			this.audio.src = null;
		}

		// We need to cancel all request, through setting the sources
		// of all streaming elements to "". Otherwise we have to many
		// concurrent requests for chrome.
		if (this.screens && this.screens.length > 0) {
			for (const screen of this.screens) {
				if (screen?.videoElement()) {
					screen.videoElement().src = null;
				}
			}
		}
	}

	private isAudioAvailable(): boolean {
		const isAvailable =
			this.scenarioRecording &&
			this.scenarioRecording.recordingScenes &&
			this.scenarioRecording.recordingScenes.length > this.sceneIndex &&
			// After conversion
			this.scenarioRecording.recordingScenes[this.sceneIndex].audioFiles &&
			this.scenarioRecording.recordingScenes[this.sceneIndex].audioFiles.length > 0 &&
			((this.audio && !this.audio.error) || (this.audio && this.audio.error.code !== 4 && !isNaN(this.audio.duration)));

		return isAvailable;
	}

	private disposeAudioGraph(destroyAll: boolean = false) {
		if (this.audioMasterGainNode) {
			this.audioMasterGainNode.disconnect();
			this.audioMasterGainNode = null;
		}

		if (this.audioMerger) {
			this.audioMerger.disconnect();
			this.audioMerger = null;
		}

		if (this.gainNodes && this.gainNodes.length > 0) {
			for (const gainNode of this.gainNodes) {
				gainNode.disconnect();
			}

			this.gainNodes = null;
		}

		if (this.analyserNodes && this.analyserNodes.length > 0) {
			for (const analyserNode of this.analyserNodes) {
				analyserNode.disconnect();
			}

			this.analyserNodes = null;
		}

		if (this.audioSplitter) {
			this.audioSplitter.disconnect();
			this.audioSplitter = null;
		}

		// See: https://stackoverflow.com/questions/38460984/problems-disconnecting-nodes-with-audiocontext-web-audio-api
		// We cannot properly disconnect the audio source and the audio context. Therefore we have to
		// reuse the instance.
		//
		//  |\__ /,|  (`\
		//  |_ _  |.--.)  )
		//  ( T   )      /
		// (((^_(((/(((_/

		if (this.audioSource) {
			this.audioSource.disconnect();
			// this.audioSource = null;
		}

		if (this.audioContext) {
			this.audioContext.destination.disconnect();

			// See: SIMSOFT-4949
			if (destroyAll) {
				this.audioContext.close();
				this.audioContext = null;
			}
		}
	}

	private disposeEventListeners() {
		if (this.unsubscribePlayerCommand) {
			this.unsubscribePlayerCommand.unsubscribe();
		}
	}

	private disposeThreads() {
		this.stopSync();
		this.stopTimer();

		if (this.audioDelayTimeout) {
			clearTimeout(this.audioDelayTimeout);
			this.audioDelayTimeout = null;
		}

		if (this.calculateVolumeInterval) {
			clearInterval(this.calculateVolumeInterval);
			this.calculateVolumeInterval = null;
		}
	}

	private calculateVolumes() {
		if (this.calculateVolumeInterval) {
			clearInterval(this.calculateVolumeInterval);
			this.calculateVolumeInterval = null;
		}
		this.calculateVolumeInterval = setInterval((args) => {
			if (this.analyserNodes && this.analyserNodes.length > 0) {
				const volumeData: {[audioInputChannelId: string]: number} = {};

				for (const analyserNode of this.analyserNodes) {
					const sampleBuffer: Float32Array = analyserNode["sampleBuffer"];

					analyserNode.getFloatTimeDomainData(sampleBuffer);

					// Compute average power over the interval.
					// let sumOfSquares = 0;
					// for (let i = 0; i < sampleBuffer.length; i++) {
					// 	sumOfSquares += sampleBuffer[i] ** 2;
					// }
					// const avgPowerDecibels = 10 * Math.log10(sumOfSquares / sampleBuffer.length);

					// Compute peak instantaneous power over the interval.
					let peakInstantaneousPower = 0;
					for (let i = 0; i < sampleBuffer.length; i++) {
						const power = sampleBuffer[i] ** 2;
						peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
					}
					const peakInstantaneousPowerDecibels = 10 * Math.log10(peakInstantaneousPower);

					volumeData[analyserNode["audioInputChannelId"]] = peakInstantaneousPowerDecibels;
				}

				this._simSharedAudioDataService.volumeData.set(volumeData);

				// console.log(JSON.stringify(volumeData));
			}
		}, 48000 / 2048);
	}

	private resetScenarioRecordingPlayerState() {
		this.sceneIndex = 0;
		this.scenarioRecordingScene = null;
		this.scenarioRecording = null;
		// This prevents the export from being loaded
		// this.scenarioRecordingExportId = null;
		this.timeProperties.reset();
		this.screens = [];
	}

	public play() {
		if (this.startPlaying) {
			return;
		}
		this.startPlaying = true;

		this.stopSync();
		this.stopTimer();

		if (this.audioDelayTimeout) {
			clearTimeout(this.audioDelayTimeout);
			this.audioDelayTimeout = null;
			this.waitingForAudioTimeout = false;
		}

		const loadResourceTimeout = 2000; // 2 seconds

		const playTasks: Promise<IVideoScreenPlayResult>[] = [];

		// This condition helps in case the play button is pressed while the videos are
		// loading. This can lead to an active play button but no playing video.
		if (this.synchronizationMasterScreen) {
			if (this.screens && this.screens.length > 0) {
				for (const screen of this.screens) {
					playTasks.push(screen.play(loadResourceTimeout));
				}

				if (this.isAudioAvailable()) {
					playTasks.push(
						new Promise((resolve) => {
							let timeoutTask = setTimeout(() => {
								resolve({
									result: PlayResult.timeout,
									source: "audio",
									screen: null,
								});
							}, loadResourceTimeout);

							this.audio.play().then(
								() => {
									console.debug("Audio is now playing");
									clearTimeout(timeoutTask);
									resolve({
										result: PlayResult.ok,
										source: "audio",
										screen: null,
									});
								},
								() => {
									console.debug("audio cannot be played!not available");
									clearTimeout(timeoutTask);
									resolve({
										result: PlayResult.unknownError,
										source: "audio",
										screen: null,
									});
								},
							);
						}),
					);
				} else {
					console.debug("audio not available");
				}

				this.startPlayingTask = Promise.all(playTasks).then((playResults) => {
					let invalidPlayResults = Enumerable.from(playResults).where((playResult) => {
						return playResult.result !== PlayResult.ok;
					});
					if (invalidPlayResults.any()) {
						invalidPlayResults.forEach((playResult) => {
							try {
								if (!!playResult.screen) {
									console.log(`Video: ${playResult.source} could not start.`);
								} else {
									this.audio.play().then(
										() => {
											//worked :)
										},
										(reason) => {
											console.log(`Audio: ${playResult.source} could not start: ${reason} ?? [NoReason]`);
										},
									);
								}
							} catch (ex) {
								console.log(ex);
							}
						});
					}

					this._emitterService.onScenarioRecordingPlayerPlaying.next();

					this.startSync();
					this.startTimer();

					const hasChromeAutoPlayPolicyProblem = Enumerable.from(playResults).any((playResult) => {
						return playResult.result === PlayResult.notAllowed;
					});

					if (hasChromeAutoPlayPolicyProblem) {
						const title = this.translate.instant(this.T.AUTO_PLAY_POLICY_TITLE);
						const description = this.translate.instant(this.T.AUTO_PLAY_POLICY_DESCRIPTION);
						const okButton = this.translate.instant(this.T.AUTO_PLAY_POLICY_OK);

						this._simMaterialDialogService.openInformationDialog({
							okText: okButton,
							title: title,
							description: description,
						});
					}

					this.timeProperties.paused = this.isPaused();
					this.broadcastTimeProperties();
				});
			} else {
				this.startPlayingTask = Promise.resolve();
			}
		} else {
			this.startPlayingTask = Promise.resolve();
		}

		this.startPlayingTask.finally(() => {
			this.startPlaying = false;
		});

		return this.startPlayingTask;
	}

	private calculateCurrentTime() {
		if (!this.isJumping && !this.isPaused()) {
			if (this.scenarioRecording) {
				// sum scene times from previous scenes
				const previousRecordingScenes = $.grep(this.scenarioRecording.recordingScenes, (recordingScene) => {
					return recordingScene.index < this.sceneIndex;
				});

				let time = this.getTotalTimeInMilliseconds(previousRecordingScenes);

				// When changing rooms there is a small timeframe, where
				// the syncMaster is null
				if (this.synchronizationMasterScreen != null) {
					time += this.synchronizationMasterScreen.currentTimeInMilliseconds;
				}

				this.timeProperties.position = time;
				this.timeProperties.scenarioRecordingSceneIndex = this.sceneIndex;

				this.broadcastTimeProperties();
			}
		}
	}

	// This function jumps to a specific point in time, indicated
	// by the 1st argument (second).
	// The 2nd argument (playAfterJumpComplete) is use for the function
	// stop() to reset the videos.
	// The 3rd argument is the success callback that will be called

	private async stop() {
		await this.jumpTo(0, false, false);

		this.calculateCurrentTime();

		// If there is currently a delay waiting to get executed we should
		// stop it also, otherwiese the audio will start playing while the videos are stopped.
		if (this.audioDelayTimeout) {
			clearTimeout(this.audioDelayTimeout);
			this.audioDelayTimeout = null;
			this.waitingForAudioTimeout = false;
		}

		this._emitterService.onScenarioRecordingPlayerStopped.next();
	}

	private pause(userInitiated: boolean = true) {
		if (!this.startPlaying) {
			this.stopSync();
			this.stopTimer();

			// Otherwise an already started timer could cause problems
			if (this.audioDelayTimeout) {
				clearTimeout(this.audioDelayTimeout);
				this.audioDelayTimeout = null;
				this.waitingForAudioTimeout = false;
			}

			if (this.screens && this.screens.length > 0) {
				for (const screen of this.screens) {
					screen.pause();
				}
			}

			// Null-Checks added
			if (this.audio && this.audio.pause) {
				this.audio.pause();
				console.debug("Audio was paused");
			}

			this.timeProperties.paused = this.isPaused();

			this.broadcastTimeProperties();

			if (userInitiated) {
				this._emitterService.onScenarioRecordingPlayerPaused.next();
			}
		} else {
			// Wait for start playing to finish and then
			// we pause the debriefing.
			if (this.waitPausing) {
				return;
			}
			this.waitPausing = true;

			this.startPlayingTask.then(() => {
				//this.pause(userInitiated);
				this.waitPausing = false;
			});
		}
	}

	private seek(millisecond: number, playAfterJumpComplete: boolean) {
		if (this.scenarioRecording) {
			const previousRecordingScenes = $.grep(this.scenarioRecording.recordingScenes, (recordingScene: any, recordingSceneIndex) => {
				return recordingScene.index < this.sceneIndex;
			});

			let time = this.getTotalTimeInMilliseconds(previousRecordingScenes);

			// set timeline
			this.timeProperties.position = millisecond;
			this.timeProperties.scenarioRecordingSceneIndex = this.sceneIndex;

			// calculate offset in current scene
			time = millisecond - time;

			// 1) set the time to jump to for all video elements
			for (const screen of this.screens) {
				screen.seekTo(time / 1000);
			}

			// 2) set the time to jump to fall the audio element
			if (this.isAudioAvailable()) {
				// 2017-05-02, Alex
				// The mixdown is over the total length of the recording,
				// so we don"t need to calculate the time inside of the current scene.
				const currentAudioTime = this.getAudioTime(time) / 1000;

				if (currentAudioTime >= 0) {
					this.audio.currentTime = currentAudioTime;
				} else {
					this.audio.currentTime = 0;
				}
			}

			if (playAfterJumpComplete) {
				this.play();
			}

			this.broadcastTimeProperties();
		}
	}

	private isPaused() {
		return !this.syncThread && !this.timerThread;
	}

	private startSync() {
		this.stopSync();
		this.syncThread = setInterval(() => {
			this.synchronizeVideoAndAudio();
		}, SimConfigurationService.settings.scenarioRecordingPlayerSynchronizationInterval);
	}

	private stopSync() {
		if (this.syncThread) {
			clearInterval(this.syncThread);
			this.syncThread = null;
		}
	}

	private startTimer() {
		this.stopTimer();
		this.timerThread = setInterval(() => {
			this.calculateCurrentTime();
		}, SimConfigurationService.settings.scenarioRecordingPlayerTimerInterval);
	}

	private stopTimer() {
		if (this.timerThread) {
			clearInterval(this.timerThread);
			this.timerThread = null;
		}
	}

	private initializeTimes() {
		this.timeProperties.totalMilliseconds = this.getTotalTimeInMilliseconds(this.scenarioRecording.recordingScenes);
		this.broadcastTimeProperties();
	}

	private async initializeMedias() {
		await Promise.all([this.initializeVideos(), this.initializeAudios()]);
	}

	private isAudioAvailableForScene(sceneIndex: number) {
		return (this.scenarioRecording?.recordingScenes?.[sceneIndex]?.audioFiles ?? []).length > 0;
	}

	private initializeAudios(): Promise<void> {
		// Dispose previous audio element
		if (this.audio) {
			document.getElementById("audio-container").removeChild(this.audio);
		}

		// Create new audio element
		this.audio = document.createElement("audio");
		this.audio.id = "audio-player";
		this.audio.controls = false;
		this.audio.playbackRate = this.playbackRate ?? 1;

		// Append audio to container
		document.getElementById("audio-container").appendChild(this.audio);

		let initializeAudioPromise: Promise<void> = null;
		let initializeAudioGraphPromise: Promise<boolean> = null;
		let waitForUserInteractionPromise: Promise<boolean> = null;

		// Check if mixdown is available.
		if (this.isAudioAvailableForScene(this.sceneIndex)) {
			this.audio.src = this.getAudioPath(this.sceneIndex);
			this.audio.currentTime = 0;
			this.audio.load();

			const recordingScene = this.scenarioRecording.recordingScenes[this.sceneIndex];

			// -------------------------------------------------------------------- //
			// Creating audio context and connect with media element source         //
			// -------------------------------------------------------------------- //
			if (!this.audioContext) {
				this.audioContext = new AudioContext();
			}

			if (this.audioContext.state !== "suspended") {
				waitForUserInteractionPromise = Promise.resolve(true);
			} else {
				const title = this.translate.instant(this.T.GLOBAL_TEXT_AUTOPLAY_POLICY_CONFIRM_DIALOG_TITLE);
				const description = this.translate.instant(this.T.GLOBAL_TEXT_AUTOPLAY_POLICY_CONFIRM_DIALOG_DESCRIPTION);
				const okText = this.translate.instant(this.T.GLOBAL_TEXT_AUTOPLAY_POLICY_CONFIRM_DIALOG_OK_TEXT);
				const cancelText = this.translate.instant(this.T.GLOBAL_TEXT_CANCEL);

				waitForUserInteractionPromise = this._simMaterialDialogService
					.openConfirmationDialog({
						title: title,
						description: description,
						okText: okText,
						cancelText: cancelText,
					})
					.then((okClicked) => {
						if (okClicked.action === "ok") {
							return new Promise((resolver) => {
								this.audioContext.resume().then(
									() => {
										resolver(true);
									},
									() => {
										resolver(false);
									},
								);
							});
						} else {
							return false;
						}
					});
			}
			// Do this only if we need multichannel audio, because customer with
			// only single channel audio do not have the chrome "require-user-gesture" option disabled.
			// See: SIMSOFT-5108
			if (this.scenarioRecording.isMultiChannelAudio) {
				/////////////////////////////////////////////////////////////////////////////////////////////////////
				//                                                                                                 //
				//     +---------------------------------+                                                         //
				//     | HTML5 Audio Tag                 |                                                         //
				//     +---------------------------------+                                                         //
				//                     |                                                                           //
				//                     |                                                                           //
				//                     v                                                                           //
				//     +---------------------------------+          Converts audio tag to consumable streaming     //
				//     | Audio Source Node               |          data, so we can attach an audio graph.         //
				//     +---------------------------------+                                                         //
				//                     |                                                                           //
				//                     |                                                                           //
				//                     v                                                                           //
				//     +---------------------------------+                                                         //
				//     | Splitter Node                   |                                                         //
				//     +---------------------------------+                                                         //
				//                     |                                                                           //
				//            +--------+-------+---------------+-----------------+                                 //
				//            v                v               v                 v                                 //
				//     +-------------+  +-------------+  +-------------+  +-------------+    Now we can control    //
				//     | Gain Node 1 |  | Gain Node 2 |  | Gain Node 3 |  | Gain Node 4 |    the mute status of    //
				//     +-------------+  --------------+  +-------------+  --------------+    each audio channel.   //
				//            |                |               |                 |                                 //
				//     +-------------+  +-------------+  +-------------+  +-------------+    Analyzer nodes are    //
				//     | Analyzer 1  |  | Analyzer 2  |  | Analyzer 3  |  | Analyzer 4  |    not connected to the  //
				//     +-------------+  --------------+  +-------------+  --------------+    merger node.          //
				//            +----------------+---------------+-----------------+                                 //
				//            |                                                                                    //
				//            v                                                                                    //
				//     +---------------------------------+                                                         //
				//     | Merger Node                     |                                                         //
				//     +---------------------------------+                                                         //
				//            |                                                                                    //
				//            v                                                                                    //
				//     +---------------------------------+          Mixes multiple channels to mono or stereo      //
				//     | Audio Worklet Node (Downmixing) |          audio. Stereo appears to be louder.            //
				//     +---------------------------------+                                                         //
				//            |                                                                                    //
				//            v                                                                                    //
				//     +---------------------------------+                                                         //
				//     | Master Gain Node                |                                                         //
				//     +---------------------------------+                                                         //
				//            |                                                                                    //
				//            v                                                                                    //
				//     +---------------------------------+                                                         //
				//     | Dynamic Compressor Node         |          Flattens peaks, which can lead to cracking     //
				//     +---------------------------------+          sounds.                                        //
				//            |                                                                                    //
				//            v                                                                                    //
				//     +---------------------------------+                                                         //
				//     | Destination (Speaker)           |                                                         //
				//     +---------------------------------+                                                         //
				//                                                                                                 //
				/////////////////////////////////////////////////////////////////////////////////////////////////////

				initializeAudioGraphPromise = waitForUserInteractionPromise.then(() => {
					return new Promise((resolve) => {
						const audioInputChannels = Enumerable.from(recordingScene.audioFiles[0]?.audioInputChannels ?? [])
							.distinct((x) => x.id)
							.toArray();

						// -------------------------------------------------------------------- //
						// Creates gain nodes                                                   //
						// -------------------------------------------------------------------- //
						const createGainNodes = () => {
							if (this.gainNodes && this.gainNodes.length > 0) {
								for (const gainNode of this.gainNodes) {
									gainNode.disconnect();
								}
							}

							this.gainNodes = [];

							for (const audioInputChannel of audioInputChannels) {
								const gainNode = this.audioContext.createGain();
								const audioChannelState = this.audioChannelStates[audioInputChannel.id];

								// Add identifier
								gainNode["audioInputChannelId"] = audioInputChannel.id;

								if (audioChannelState && !audioChannelState.muted) {
									SimUtilityService.setGainNodeValue(gainNode, audioChannelState?.actualVolume);
								} else {
									SimUtilityService.setGainNodeValue(gainNode, 0);
								}

								this.gainNodes.push(gainNode);
							}
						};

						// -------------------------------------------------------------------- //
						// Creates analyser nodes                                               //
						// -------------------------------------------------------------------- //
						const createAnalyserNodes = () => {
							if (this.analyserNodes && this.analyserNodes.length > 0) {
								for (const analyserNode of this.analyserNodes) {
									analyserNode.disconnect();
								}
							}

							this.analyserNodes = [];

							for (const audioInputChannel of audioInputChannels) {
								const analyserNode = this.audioContext.createAnalyser();

								// Add identifier
								analyserNode["audioInputChannelId"] = audioInputChannel.id;
								analyserNode["sampleBuffer"] = new Float32Array(analyserNode.fftSize);

								this.analyserNodes.push(analyserNode);
							}
						};

						// -------------------------------------------------------------------- //
						// Connect splitter to gain nodes                                       //
						// -------------------------------------------------------------------- //
						const connectSplitterNodeToGainNodes = () => {
							if (this.audioSplitter) {
								this.audioSplitter.disconnect();
							}

							for (
								let audioInputChannelIndex = 0;
								audioInputChannelIndex < audioInputChannels.length;
								audioInputChannelIndex++
							) {
								const gainNode = this.gainNodes[audioInputChannelIndex];

								this.audioSplitter.connect(gainNode, audioInputChannelIndex);
							}
						};

						const connectGainNodesToAnalyzerNodes = () => {
							for (
								let audioInputChannelIndex = 0;
								audioInputChannelIndex < audioInputChannels.length;
								audioInputChannelIndex++
							) {
								const gainNode = this.gainNodes[audioInputChannelIndex];
								const analyserNode = this.analyserNodes[audioInputChannelIndex];

								if (gainNode) {
									gainNode.disconnect();
								}

								gainNode.connect(analyserNode);
							}
						};

						// -------------------------------------------------------------------- //
						// Connect gain nodes to merger node                                    //
						// -------------------------------------------------------------------- //
						const connectGainNodesToMergerNode = () => {
							for (
								let audioInputChannelIndex = 0;
								audioInputChannelIndex < audioInputChannels.length;
								audioInputChannelIndex++
							) {
								const gainNode = this.gainNodes[audioInputChannelIndex];

								gainNode.connect(this.audioMerger, 0, audioInputChannelIndex);
							}
						};

						// -------------------------------------------------------------------- //
						// Create and connect splitter node                                     //
						// -------------------------------------------------------------------- //
						const createChannelSplitter = () => {
							if (this.audioSplitter) {
								this.audioSplitter.disconnect();
							}

							this.audioSplitter = this.audioContext.createChannelSplitter(audioInputChannels.length);
						};

						const createAudioSource = () => {
							if (this.audioSource) {
								this.audioSource.disconnect();
							}

							this.audioSource = this.audioContext.createMediaElementSource(this.audio);
						};

						const connectAudioSource = () => {
							this.audioSource.connect(this.audioSplitter);
						};

						const createAudioMerger = () => {
							if (this.audioMerger) {
								this.audioMerger.disconnect();
							}

							this.audioMerger = this.audioContext.createChannelMerger(audioInputChannels.length);
						};

						const connectAudioMergerToAudioWorklet = () => {
							// -------------------------------------------------------------------- //
							// Connect merger node to audio worklet node to mixdown the channels.   //
							// This is necessary to ensure that als browsers and plattform hear all //
							// channels. (For example: clients without 5.1 speaker setup but 6      //
							// channel audio)                                                       //
							// -------------------------------------------------------------------- //
							this.audioMerger.connect(this.audioWorkletNode);
						};

						// -------------------------------------------------------------------- //
						// Set gain for all nodes to 1 (full volume)                            //
						// -------------------------------------------------------------------- //
						const unmuteGainNodes = () => {
							for (
								let audioInputChannelIndex = 0;
								audioInputChannelIndex < audioInputChannels.length;
								audioInputChannelIndex++
							) {
								const audioInputChannel = audioInputChannels[audioInputChannelIndex];

								const gainNode = this.gainNodes[audioInputChannelIndex];

								// See: SIMSOFT-5183
								if (audioInputChannel["muted"]) {
									SimUtilityService.setGainNodeValue(gainNode, 0);
								} else {
									SimUtilityService.setGainNodeValue(gainNode, 100);
								}
							}
						};

						// -------------------------------------------------------------------- //
						// Audio worklet                                                        //
						// -------------------------------------------------------------------- //
						const createAudioWorkletMixdown = () => {
							if (this.audioWorkletNode) {
								this.audioWorkletNode.disconnect();
							}

							this.audioWorkletNode = new AudioWorkletNode(this.audioContext, "audio-worklet-down-mixer", {
								processorOptions: {
									// Allowed values: avg or add
									mixingMode: SimConfigurationService.settings.audioMixingMode,

									// Allowed values: mono or stereo
									outputMode: SimConfigurationService.settings.audioOutputMode,
								},
								numberOfOutputs: SimConfigurationService.settings.audioOutputMode === "stereo" ? 2 : 1,
							});
						};

						const createMasterGainNode = () => {
							if (this.audioMasterGainNode) {
								this.audioMasterGainNode.disconnect();
							}

							this.audioMasterGainNode = this.audioContext.createGain();

							SimUtilityService.setGainNodeValue(this.audioMasterGainNode, 100);
						};

						const connectAudioWorkletToMasterGainNode = () => {
							this.audioWorkletNode.connect(this.audioMasterGainNode);
						};

						const createDynamicCompressorNode = () => {
							if (this.audioDynamicCompressorNode) {
								this.audioDynamicCompressorNode.disconnect();
							}

							this.audioDynamicCompressorNode = this.audioContext.createDynamicsCompressor();

							this.audioDynamicCompressorNode.connect(this.audioContext.destination);
						};

						const connectMasterGainNodeToDynamicCompressorNode = () => {
							this.audioMasterGainNode.connect(this.audioDynamicCompressorNode);
						};

						if (audioInputChannels && audioInputChannels.length > 0) {
							let addModulePromise: Promise<void>;

							if (!this.audioWorkletNode) {
								addModulePromise = this.audioContext.audioWorklet.addModule(
									environment.baseHref + "/worklets/audio-worklet-down-mixer-module.js",
								);
							} else {
								addModulePromise = Promise.resolve();
							}

							addModulePromise.then(
								() => {
									createAudioSource();

									// -------------------------------------------------------------------- //
									// Creating gain nodes                                                  //
									// -------------------------------------------------------------------- //
									createGainNodes();

									// -------------------------------------------------------------------- //
									// Creating analyser nodes                                              //
									// -------------------------------------------------------------------- //
									createAnalyserNodes();

									// -------------------------------------------------------------------- //
									// Split audio                                                          //
									// -------------------------------------------------------------------- //
									createChannelSplitter();

									// -------------------------------------------------------------------- //
									// AudioSource -> SplitterNode                                          //
									// -------------------------------------------------------------------- //
									connectAudioSource();

									// -------------------------------------------------------------------- //
									// Connect splitter with gain nodes                                     //
									// -------------------------------------------------------------------- //
									connectSplitterNodeToGainNodes();

									// -------------------------------------------------------------------- //
									// Connect analyser nodes to gain nodes                                 //
									// -------------------------------------------------------------------- //
									connectGainNodesToAnalyzerNodes();

									// -------------------------------------------------------------------- //
									// Create merge audio node                                              //
									// -------------------------------------------------------------------- //
									createAudioMerger();

									// -------------------------------------------------------------------- //
									// Connect gain nodes with merger                                       //
									// -------------------------------------------------------------------- //
									connectGainNodesToMergerNode();

									// -------------------------------------------------------------------- //
									// Connect audio worklet node with destination (speaker)                //
									// -------------------------------------------------------------------- //
									createAudioWorkletMixdown();

									// -------------------------------------------------------------------- //
									// Connect merger node to audio worklet                                 //
									// -------------------------------------------------------------------- //
									connectAudioMergerToAudioWorklet();

									createMasterGainNode();

									connectAudioWorkletToMasterGainNode();

									createDynamicCompressorNode();

									connectMasterGainNodeToDynamicCompressorNode();

									// -------------------------------------------------------------------- //
									// Set gain for all nodes to 1 (full volume)                            //
									// -------------------------------------------------------------------- //
									unmuteGainNodes();

									resolve(true);
								},
								(reason) => {
									console.log(reason);
								},
							);
						} else {
							resolve(true);
						}
					});
				});
			} else {
				initializeAudioGraphPromise = Promise.resolve(true);
			}

			initializeAudioPromise = initializeAudioGraphPromise.then(() => {
				this.handleAudioErrorEventsFunction = this.handleAudioErrorEvents.bind(this);

				this.audio.addEventListener("abort", this.handleAudioErrorEventsFunction);
				this.audio.addEventListener("cancel", this.handleAudioErrorEventsFunction);
				this.audio.addEventListener("error", this.handleAudioErrorEventsFunction);
				this.audio.addEventListener("stalled", this.handleAudioErrorEventsFunction);
				this.audio.addEventListener("suspend", this.handleAudioErrorEventsFunction);

				if (
					SimConfigurationService.settings.dynamicAudioDelay &&
					this.scenarioRecording.recordingScenes &&
					this.scenarioRecording.recordingScenes.length > this.sceneIndex &&
					this.scenarioRecording.recordingScenes[this.sceneIndex].videoFiles &&
					this.scenarioRecording.recordingScenes[this.sceneIndex].videoFiles.length > 0 &&
					this.scenarioRecording.recordingScenes[this.sceneIndex].audioFiles &&
					this.scenarioRecording.recordingScenes[this.sceneIndex].audioFiles.length > 0
				) {
					// 2019-05-08, Alex
					// We use the longest durations, because they are most likely to be the correct length.
					const longestVideoFileDuration = Enumerable.from(this.scenarioRecording.recordingScenes[this.sceneIndex].videoFiles)
						.select((videoFile) => videoFile.duration)
						.defaultIfEmpty(0)
						.max();

					const shortestAudioFileDuration = Enumerable.from(this.scenarioRecording.recordingScenes[this.sceneIndex].audioFiles)
						.select((audioFile) => audioFile.duration)
						.defaultIfEmpty(0)
						.min();

					this.audioOffset = longestVideoFileDuration - shortestAudioFileDuration;
				} else {
					this.audioOffset = this.scenarioRecording.audioDelay;
				}
			});
		} else {
			this.audio.src = "";
			this.audio.load();

			this.audio = null;

			initializeAudioPromise = Promise.resolve();
		}

		return initializeAudioPromise.then(() => {
			console.debug("Audio initialized");
		});
	}

	private getTotalTimeInMilliseconds(recordingScenes: RecordingScene[]) {
		let sum: number;

		if (recordingScenes && recordingScenes.length > 0) {
			sum = Enumerable.from(recordingScenes).sum((recordingScene) => {
				return recordingScene.totalRecordingSceneTimeMs;
			});
		} else {
			sum = 0;
		}

		return sum;
	}

	private broadcastTimeProperties() {
		this._emitterService.onScenarioRecordingPlayerTimeUpdated.next(this.timeProperties);
	}

	private getAudioPath(recordingSceneIndex: number) {
		if (this.scenarioRecording) {
			return this._simUtilityService.getScenarioRecordingAudioUrl(this.scenarioRecording.id, recordingSceneIndex);
		} else {
			return "";
		}
	}

	private initializeVideos() {
		return new Promise((resolve, reject) => {
			this.screens = this.getScreensForRecordingScene(this.scenarioRecording, this.sceneIndex);
			this.setFullscreenForCameraViewMode();

			let limit = 1000;
			const finishWaiterPromise = setInterval(() => {
				// --------------------------------------------------------------- //
				// 2018-09-13, Alex                                                //
				// --------------------------------------------------------------- //
				// Problem:                                                        //
				// Can't play a scenario recording if the first scene contains no  //
				// videos.                                                         //
				//                                                                 //
				// Solution:                                                       //
				// Check if scene has any video, otherwise skip this scene.        //
				// --------------------------------------------------------------- //

				const hasVideosInCurrentScene = Enumerable.from(this.screens).any((screen) => {
					return screen.url !== SimConfigurationService.settings.noVideoUrl;
				});

				if (hasVideosInCurrentScene) {
					const readyScreenCount = Enumerable.from(this.screens)
						.where((screen) => {
							return screen.isReady;
						})
						.count();

					if (readyScreenCount === this.screens.length) {
						// 2019-05-08, Alex
						// We use the longest durations because they are most likely to be the correct length.
						// Sometimes we get following video durations:
						// [33800, 33134, 33033, 33034] the first duration is from an vital monitor and is considerably longer
						// than the recordings made from an axis camera. Therefore we do not consider it as master.
						// If we would use this screens to synchronize the audio the would appear out of sync.
						// The other reason is that if the vital monitor is a little bit out of sync, nobody will notice (no lip movement, etc).
						this.synchronizationMasterScreen = Enumerable.from(this.screens)
							.where((screen) => {
								return screen.mediaType == "streamable";
							})
							.orderByDescending((screen) => {
								return screen.videoDuration;
							})
							.firstOrDefault();

						if (this.synchronizationMasterScreen) {
							this.synchronizationMasterScreen.isSynchronizationMaster = true;
							this.synchronizationMasterScreen.onEnded = async () => {
								this.pause(false);

								// load next scene or stop if the current was the last
								if (this.sceneIndex < this.scenarioRecording.recordingScenes.length - 1) {
									const synchronizationChecker = setInterval(async () => {
										if (!this.syncing) {
											clearInterval(synchronizationChecker);

											let videoInitialized = false;

											while (
												this.sceneIndex + 1 < this.scenarioRecording.recordingScenes.length &&
												!videoInitialized
											) {
												this.sceneIndex++;

												// 2018-09-10, Alex
												// Check if scene has videos, if not we must skip the scene to
												// prevent the video from freezing.
												// https://simstation.atlassian.net/browse/SIMSOFT-2210

												this.scenarioRecordingScene = this.scenarioRecording.recordingScenes[this.sceneIndex];

												if (this.scenarioRecordingScene) {
													if (
														this.scenarioRecordingScene.videoFiles &&
														this.scenarioRecordingScene.videoFiles.length > 0
													) {
														videoInitialized = true;

														this.initializeMedias().then(() => {
															this.play();
														});
													} else {
														this._simNotificationService.notifyInfo(
															this.translate.instant(
																this.T.SCENARIO_RECORDING_CONTROLLER_SCREEN_SKIPPED_NO_VIDEOS_AVAILABLE,
															),
														);

														// if the last scene has no videos we have to skip this and
														// set the position to the total recording time. Otherwise
														// the position would stay in the middle of the scenario recording.
														if (this.sceneIndex + 1 === this.scenarioRecording.recordingScenes.length) {
															this.timeProperties.position = this.scenarioRecording.recordingTimeMs;
															this.timeProperties.scenarioRecordingSceneIndex = this.sceneIndex;

															this.broadcastTimeProperties();

															// Rewind
															await this.stop();
														}
													}
												}
											}
										}
									}, 100);
								} else {
									// Rewind
									await this.stop();

									this.sceneIndex = 0;
									this.scenarioRecordingScene = this.scenarioRecording.recordingScenes[this.sceneIndex];

									this._emitterService.onScenarioRecordingPlayerFinished.next();

									// 2019-03-26, Alex
									// If we have multiple scene and we want to replay the same scenario recording,
									// we have to reinitialize the video and audio files. Otherwise the debriefing will start
									// with the last scene of the scenario recording.
									await this.initializeMedias();
								}
							};
						}

						resolve({success: true, reason: null});

						clearInterval(finishWaiterPromise);
					}

					limit--;

					// Emergency exit.
					if (limit <= 0) {
						clearInterval(finishWaiterPromise);

						// resolve({ success: false, reason: `ready-count was never reached. Max was: ${readyScreenCount} of ${this.screens.length}` });
						reject();
					}
				} else {
					// 2019-11-26, Alex
					// Null-Check added
					if (this.sceneIndex < (this.scenarioRecording?.recordingScenes || []).length) {
						this.sceneIndex++;
						this.screens = this.getScreensForRecordingScene(this.scenarioRecording, this.sceneIndex);
					} else {
						reject();
					}
				}
			}, 200);
		}).then(() => {
			console.debug("Video initialized");
			setTimeout(() => {
				this.updatePlaybackRate();
			});
		});
	}

	protected setFullscreenForCameraViewMode() {
		if (this.screens) {
			let screen: IVideoScreen = null;
			try {
				if (this.cameraViewMode !== CameraViewMode.AllScreens) {
					screen = this.screens?.[this.cameraViewMode - 1];
				}
			} catch (e) {}

			this._emitterService?.onSelectedFullScreenChanged.next({
				currentFullScreen: screen,
			});
		}
	}

	private getScreensForRecordingScene(scenarioRecording: ScenarioRecording, recordingSceneIndex: number) {
		const screens: VideoScreen[] = [];

		if (this.scenarioRecording) {
			const recordingScene = this.getRecordingSceneByIndex(recordingSceneIndex);

			if (recordingScene) {
				const maxScreens = this._simEnumService.getMaxScreenCountForCameraLayoutType(recordingScene.cameraLayout);

				const longestVideoFileDuration = Enumerable.from(recordingScene.videoFiles)
					.select((videoFile) => videoFile.duration)
					.defaultIfEmpty(0)
					.max();

				for (let screenIndex = 0; screenIndex < maxScreens; screenIndex++) {
					const videoFile = Enumerable.from(recordingScene.videoFiles).firstOrDefault((file) => file.videoIndex === screenIndex);
					let url: string = null;
					let mediaType = null;
					let videoDuration = 0;

					if (videoFile) {
						videoDuration = videoFile.duration;

						if (videoFile.isValid && videoFile.downloaded) {
							if (!scenarioRecording["isTransformedForExport"]) {
								url = this._simUtilityService.getScenarioRecordingVideoUrl(
									this.scenarioRecording.id,
									recordingSceneIndex,
									screenIndex,
								);
							} else {
								url = this._simUtilityService.getScenarioRecordingVideoUrl(this.scenarioRecording.id, 0, 0);
							}

							mediaType = "streamable";
						} else {
							mediaType = "picture";
							url = SimConfigurationService.settings.noVideoUrl;
						}
					} else {
						mediaType = "picture";
						url = SimConfigurationService.settings.noVideoUrl;
					}

					// isReady will be set to true if the video-tag can play the video, or the video failed with an error.
					// the property will be set in the directive that displays the screen. canPlay will only be set
					// if and only if the video can be played.
					const screen = new VideoScreen();

					screen.url = url;
					screen.videoDuration = videoDuration;
					screen.videoOffset = longestVideoFileDuration - videoDuration;
					screen.mediaType = mediaType;
					screen.screenIndex = screenIndex;
					screen.isVitalMonitor = screenIndex === recordingScene.vitalMonitorIndex;

					screens.push(screen);
				}
			}
		}

		return screens;
	}

	private getRecordingSceneByIndex(recordingSceneIndex: number): RecordingScene {
		if (this.scenarioRecording?.name && this.scenarioRecording?.recordingScenes?.length >= recordingSceneIndex) {
			const recordingScene = Enumerable.from(this.scenarioRecording.recordingScenes)
				.where((scene) => {
					return scene.index === recordingSceneIndex;
				})
				.firstOrDefault();

			return recordingScene;
		} else {
			return null;
		}
	}

	private synchronizeVideoAndAudio() {
		this.syncing = true;

		// When changing rooms there is a small timeframe, where
		// the syncMaster is null
		if (this.synchronizationMasterScreen != null && !this.synchronizationMasterScreen.isPaused) {
			// relative in a sense that it start on 0 on every scene
			const relativeMasterPosition = this.synchronizationMasterScreen.currentTimeInMilliseconds;

			// Synchronize video
			// =============================================
			for (const screen of this.screens) {
				// We check "canPlay", because we do not need to synchronize
				// missing video files.
				if (!screen.isSynchronizationMaster && screen.canPlay) {
					const currentScreenPosition = screen.currentTimeInMilliseconds;

					// videoOffset is the difference between the longest video duration (master video)
					// and the current video duration, therefore the value can only be positive.
					//
					// We have to add the offset to the current video position to get the position
					// relative to the master video.
					//
					// V                |-------X-------------------------------------|
					//        |-Offset--|
					//         Offset 6s   Position 4s  ----> 10s
					//
					// M      |-----------------X-------------------------------------|
					//                     Position 10s ----> 10s

					const currentScreenPositionWithOffset = currentScreenPosition + screen.videoOffset;

					// Calculate difference between master and the current video.
					// Only if the threshold is reached a synchronization is attempted.
					const deviation = currentScreenPositionWithOffset - relativeMasterPosition;
					const synchronizationThreshold = SimConfigurationService.settings.scenarioRecordingPlayerSynchronizationThreshold;

					const videoNeedsSync = Math.abs(deviation) > synchronizationThreshold;
					if (videoNeedsSync) {
						const relativeMasterPositionWithOffset = relativeMasterPosition - screen.videoOffset;

						console.log("Need for video synchronization.");
						// We have to convert the time from milliseconds to seconds for the
						// html5 video element (it only understands seconds)
						screen.seekTo(relativeMasterPositionWithOffset / 1000);

						if (screen.isPaused) {
							console.debug(`Video was paused - start playing!`);
							screen.play(SimConfigurationService.settings.scenarioRecordingPlayerSynchronizationInterval).then();
						}
					}
				}
			}

			// Synchronize audio
			// =============================================
			if (this.isAudioAvailable()) {
				let currentAudioPosition = 0,
					currentVideoPosition = 0,
					synchronizationDelta = 0,
					audioNeedsSync = false;

				if (this.audioOffset >= 0) {
					currentAudioPosition = this.audio.currentTime * 1000 + this.audioOffset;
					currentVideoPosition = relativeMasterPosition;
				} else {
					currentAudioPosition = this.audio.currentTime * 1000 - Math.abs(this.audioOffset);
					currentVideoPosition = relativeMasterPosition;
				}

				// Now the current video and audio position should be the same.

				// Mixdown contains the audio over the total recording so, we have to calculate the
				// threshold with the total time in mind.
				synchronizationDelta = currentAudioPosition - currentVideoPosition;

				audioNeedsSync =
					Math.abs(synchronizationDelta) > SimConfigurationService.settings.scenarioRecordingPlayerSynchronizationThreshold;

				// @TODO: Check buffer
				if (!this.audio.seeking) {
					if (audioNeedsSync && !this.waitingForAudioTimeout && this.audio.readyState >= 2) {
						const currentAudioTime = this.getAudioTime(relativeMasterPosition) / 1000;

						if (this.enableDebug) {
							const positionText = synchronizationDelta <= 0 ? "behind" : "ahead";
							const logEntry =
								`Synchronize Audio: audio is ${positionText} for: ${synchronizationDelta}ms` +
								"\n" +
								`audio - offset: ${this.audioOffset}ms` +
								"\n" +
								`video - position: ${currentVideoPosition}ms` +
								"\n" +
								`audio - position: ${currentAudioPosition}ms` +
								"\n" +
								`new audio position: ${currentAudioTime}sec` +
								"\n" +
								`currently seeking: ${this.audio.seeking}`;

							console.debug(logEntry);
						}

						if (currentAudioTime >= 0) {
							// --------------------------------------------------------------- //
							// If the audio is behind the video we have to skip forward        //
							// --------------------------------------------------------------- //
							// 0      200      400     600     800     1000 Milliseconds       //
							//                      |------> video position (450ms)            //
							//               |--> audio position (360ms)                       //
							// --------------------------------------------------------------- //

							// We should not call load
							// See: https://www.w3schools.com/tags/av_met_load.asp

							// Old code:

							// if (this.isAudioPositionBuffered(currentAudioTime * 1000)) {
							//    this.audio.load();
							//    this.audio.currentTime = currentAudioTime;
							// } else {
							//    console.warn("No buffered.");
							// }

							// New code:
							this.audio.currentTime = currentAudioTime;
							if (this.audio.paused) {
								console.debug(`Audio was paused - start playing!`);
								this.audio.play().then();
							}
						} else {
							// --------------------------------------------------------------- //
							// If the audio is behind the video but the time is negative       //
							// because of an big delay, we have to wait till the audio         //
							// can be played                                                   //
							// --------------------------------------------------------------- //
							//  -400     -200       0      200      400     600 Milliseconds   //
							//                      |------> video position (0ms)              //
							//               |--> audio position (-160ms)                      //
							// --------------------------------------------------------------- //

							if (!this.audioDelayTimeout) {
								const audioDelta = Math.abs(synchronizationDelta);

								this.audio.pause();

								console.debug(`Wait for ${audioDelta} till audio time gets from negative to positive.`);

								this.waitingForAudioTimeout = true;

								this.audioDelayTimeout = setTimeout(() => {
									this.audio
										.play()
										.then(
											() => {
												console.debug(`Audio resumed.`);
											},
											(reason) => {
												console.log(`Could not play audio. Reason: ${reason}`);
											},
										)
										.finally(() => {
											clearTimeout(this.audioDelayTimeout);
											this.audioDelayTimeout = null;

											this.waitingForAudioTimeout = false;
										});
								}, audioDelta);
							} else {
								console.log("Waiting for audio time to get positive ...");
							}
						}
					}
				} else {
					console.log("Can't update audio position, because it is currently seeking.");
				}
			}
		} else if (this.synchronizationMasterScreen?.isPaused) {
			// If the master video is paused, we have to pause all other videos.
			for (const screen of this.screens) {
				if (!screen.isSynchronizationMaster) {
					screen.pause();
				}
			}
			this.audio.pause();
		}

		this.syncing = false;
	}

	private getAudioTime(videoTime: number): number {
		let audioTime = 0;

		if (videoTime + this.audioOffset <= this.scenarioRecording.recordingTimeMs) {
			// ==================================================== //
			// videoTime | audioOffset | operation | audioTime | ok //
			// ==================================================== //
			//       100 |         -50 |     +     |        50 |  y //
			//       100 |         +50 |     +     |       150 |  y //
			//       100 |         -50 |     -     |       150 |  n //
			//       100 |         +50 |     -     |       150 |  n //
			// ==================================================== //

			if (this.audioOffset >= 0) {
				// ========================================================================================================================== //
				//                                                                                                                            //
				// |----------------------------------------------------------|------------------------------------------------> Video        //
				// ( positive Audio offset ) |--------------------------------|------------------------------------------------> Audio        //
				//                                                                                                                            //
				// The offset will be calculated by subtracting video duration by audio duration.                                             //
				//                                                                                                                            //
				// ========================================================================================================================== //

				audioTime = videoTime - this.audioOffset;
			} else {
				// ========================================================================================================================== //
				//                                                                                                                            //
				// ( negative Audio offset ) |--------------------------------|------------------------------------------------> Video        //
				// |----------------------------------------------------------|------------------------------------------------> Audio        //
				//                                                                                                                            //
				// The offset will be calculated by subtracting video duration by audio duration.                                             //
				//                                                                                                                            //
				// ========================================================================================================================== //

				audioTime = videoTime + Math.abs(this.audioOffset);
			}
		} else {
			audioTime = this.scenarioRecording.recordingTimeMs;
		}

		return audioTime;
	}

	private getSceneIndex(millisecond: number) {
		let recordingSceneIndex = 0,
			found = false,
			globalStart: dayjs.Dayjs = null,
			position: dayjs.Dayjs = null;

		// 2018-06-18, Alex
		// Null-Check added
		if (this.scenarioRecording) {
			globalStart = dayjs(SimUtilityService.getRecordingStart(this.scenarioRecording));
			position = globalStart.add(millisecond, "milliseconds");

			for (let i = 0; !found && i < this.scenarioRecording.recordingScenes.length; i++) {
				const recordingScene = this.scenarioRecording.recordingScenes[i];

				const sceneStart = dayjs(recordingScene.start);
				const sceneEnd = dayjs(recordingScene.end);

				if ((position.isSame(sceneStart, "millisecond") || position.isAfter(sceneStart)) && position.isBefore(sceneEnd)) {
					found = true;
					recordingSceneIndex = i;
				}
			}
		}

		return recordingSceneIndex;
	}

	private setMasterVolume(volume: number) {
		if (this.audioMasterGainNode) {
			SimUtilityService.setGainNodeValue(this.audioMasterGainNode, volume);
		} else {
			SimUtilityService.setAudioElementVolume(this.audio, volume);
		}
	}
}

class VideoScreen implements IVideoScreen {
	#videoElementRef: Signal<ElementRef<HTMLVideoElement | null>> = signal(null);
	readonly videoElement = computed(() => this.#videoElementRef()?.nativeElement);

	url: string = null;
	videoDuration = 0;
	videoOffset: number = null;

	mediaType: "streamable" | "picture";

	isFullScreen = false;
	isHidden = false;

	screenIndex = -1;
	isVitalMonitor = false;
	isSynchronizationMaster = false;

	onEnded = null;

	setVideoElementRef(value: Signal<ElementRef<HTMLVideoElement | null>>): void {
		this.#videoElementRef = value;
	}

	public play(timeout: number): Promise<IVideoScreenPlayResult> {
		let timeoutTask: number;
		let ret = new Promise<IVideoScreenPlayResult>(async (resolve) => {
			timeoutTask = setTimeout(() => {
				resolve({result: PlayResult.timeout, source: source, screen: this});
			}, timeout);

			const source = "video: " + this.url;

			if (this.canPlay && this.videoElement()) {
				this.videoElement()
					.play()
					.then(
						() => {
							resolve({result: PlayResult.ok, source: source, screen: this});
						},
						(reason) => {
							if (reason && reason.name === "NotAllowedError") {
								resolve({
									result: PlayResult.notAllowed,
									source: source,
									screen: this,
								});
							} else {
								resolve({
									result: PlayResult.unknownError,
									source: source,
									screen: this,
								});
							}
						},
					);
			} else if (this.mediaType == "picture") {
				// Images are automatically ok, we do not play them, we display them.
				resolve({result: PlayResult.ok, source: source, screen: this});
			} else if (!this.isReady) {
				resolve({result: PlayResult.isNotReady, source: source, screen: this});
			} else if (!this.videoElement()) {
				resolve({
					result: PlayResult.mediaElementNotFound,
					source: source,
					screen: this,
				});
			} else if (!this.canPlay) {
				if (timeout > 0) {
					//add a delay for videos to buffer
					timeout = timeout - 100;
					await SimUtilityService.delay(100);
					let result = await this.play(timeout);
					resolve(result);
				} else {
					resolve({result: PlayResult.canNotPlay, source: source, screen: this});
				}
			} else {
				resolve({result: PlayResult.unknownError, source: source, screen: this});
			}
		});
		ret.finally(() => {
			clearTimeout(timeoutTask);
		});
		return ret;
	}

	public pause() {
		if (this.isReady && this.videoElement()) {
			this.videoElement().pause();
		}
	}

	public seekTo(positionInSeconds: number) {
		if (this.isReady && this.videoElement()) {
			this.videoElement().currentTime = positionInSeconds;
		}
	}

	public setPlaybackRate(rate: number) {
		if (!this.videoElement()) {
			return false;
		}

		this.videoElement().playbackRate = rate;
		return true;
	}

	get isReady(): boolean {
		return this.mediaType == "picture" || !!this.videoElement();
	}

	get canPlay(): boolean {
		return this.videoElement()?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
	}

	get isPaused(): boolean {
		return this.videoElement()?.paused ?? false;
	}

	get currentTimeInMilliseconds(): number {
		const currentTimeInSec = this.videoElement()?.currentTime ?? 0;
		return currentTimeInSec * 1000;
	}
}
